// /recipes — Admin Recipe Management (PAGES.md §3.62)
//
// Same as POS recipes (§2.21) + StatCards (avg food cost %, total recipes, linked items).
// RESTAURANT vertical only — MANAGER+ (level 30).
// Traceability: FR-RECIPE-001 to FR-RECIPE-008

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  PageContainer,
  Button,
  Badge,
  Select,
  Skeleton,
  EmptyState,
  ErrorBoundary,
  DataTable,
  Pagination,
  Tooltip,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import { recipeService } from '@/services/recipe.service';
import type { RecipeSummary, RecipeStats } from '@/services/recipe.service';
import { deriveRecipeKpis } from './recipe-stats';
import { useDashboardStore } from '@/stores/use-dashboard-store';
import { GLASS, cn, toneClasses } from '@/constants/design-tokens';
import { formatPaise } from '@/utils/currency';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { ApiError } from '@/lib/api-client';

// ─── Icons ──────────────────────────────────────────────────────────────────

function PlusIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
  );
}

function BookIcon() {
  return (
    <svg className="h-14 w-14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 19.5A2.5 2.5 0 016.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" />
      <line x1="8" y1="7" x2="16" y2="7" />
      <line x1="8" y1="11" x2="14" y2="11" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

// ─── KPI Card ──────────────────────────────────────────────────────────────

function KpiMini({ label, value, tone }: { label: string; value: string | number; tone: 'brand' | 'success' | 'danger' | 'violet' | 'teal' }) {
  const tc = toneClasses(tone);
  return (
    <div className={cn(GLASS, 'relative overflow-hidden p-4')}>
      <div className={`absolute inset-0 bg-gradient-to-br ${tc.accent} pointer-events-none`} />
      <p className="relative text-xs font-medium text-neutral-500 dark:text-neutral-400">{label}</p>
      <p className="relative text-xl font-bold text-neutral-900 dark:text-neutral-100 tabular-nums font-mono mt-1">{value}</p>
    </div>
  );
}

// ─── Constants ─────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

const CATEGORY_OPTIONS = [
  { value: '', label: 'All Categories' },
  { value: 'Starters', label: 'Starters' },
  { value: 'Main Course', label: 'Main Course' },
  { value: 'Beverages', label: 'Beverages' },
  { value: 'Desserts', label: 'Desserts' },
  { value: 'Breads', label: 'Breads' },
  { value: 'Rice', label: 'Rice' },
  { value: 'Sides', label: 'Sides' },
];

// ─── Content ────────────────────────────────────────────────────────────────

function RecipeListContent() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const [searchParams, setSearchParams] = useSearchParams();
  useDashboardStore((s) => s.selectedLocationId);

  const urlSearch = searchParams.get('search') ?? '';
  const urlPage = Number(searchParams.get('page') ?? '1');
  const urlCategory = searchParams.get('category') ?? '';

  const [search, setSearch] = useState(urlSearch);
  const [page, setPage] = useState(urlPage);
  const [category, setCategory] = useState(urlCategory);
  const [recipes, setRecipes] = useState<RecipeSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState<RecipeStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const searchRef = useRef<HTMLInputElement>(null);
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  useEffect(() => {
    const params: Record<string, string> = {};
    if (search) params.search = search;
    if (page > 1) params.page = String(page);
    if (category) params.category = category;
    setSearchParamsRef.current(params, { replace: true });
  }, [search, page, category]);

  const fetchRecipes = useCallback(async () => {
    setIsLoading(true);
    try {
      const [result, statsResult] = await Promise.all([
        recipeService.listRecipes({
          search: search || undefined,
          category: category || undefined,
          page,
          limit: PAGE_SIZE,
        }),
        recipeService.getStats({
          search: search || undefined,
          category: category || undefined,
        }),
      ]);
      setRecipes(result.data);
      setTotal(result.meta?.total ?? result.data.length);
      setStats(statsResult);
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to load recipes.', variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [search, category, page, toast]);

  useEffect(() => { fetchRecipes(); }, [fetchRecipes]);

  // Keyboard
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable
      )
        return;
      if (e.key === 'n') { e.preventDefault(); navigate('/recipes/new'); }
      else if (e.key === '/') { e.preventDefault(); searchRef.current?.focus(); }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate]);

  // KPIs — derived from the tenant-wide `/recipes/stats` aggregate (not the
  // current page slice) so the cards reflect every recipe and stay constant as
  // you paginate (GitHub issue #283). `total` still drives the header/pagination.
  const { totalRecipes, linkedItems, avgFoodCost } = deriveRecipeKpis(stats);

  const columns: ColumnDef<RecipeSummary>[] = useMemo(() => [
    {
      key: 'name', header: 'Recipe Name',
      render: (_v: unknown, row: RecipeSummary) => (
        <button type="button" onClick={() => navigate(`/recipes/${row.id}`)}
          className="font-bold text-neutral-900 dark:text-neutral-100 hover:text-violet-600 dark:hover:text-violet-400 transition-colors text-left">
          {row.name}
        </button>
      ),
    },
    {
      key: 'product_name', header: 'Menu Item',
      render: (_v: unknown, row: RecipeSummary) => (
        <span className="text-neutral-600 dark:text-neutral-400">{row.product_name ?? '—'}</span>
      ),
    },
    { key: 'ingredient_count', header: 'Ingredients', numeric: true,
      render: (_v: unknown, row: RecipeSummary) => <span className="font-mono tabular-nums">{row.ingredient_count}</span>,
    },
    { key: 'yield_quantity', header: 'Yield', numeric: true,
      render: (_v: unknown, row: RecipeSummary) => <span className="font-mono tabular-nums">{row.yield_quantity}</span>,
    },
    { key: 'total_cost', header: 'Food Cost', numeric: true,
      render: (_v: unknown, row: RecipeSummary) => <span className="font-mono tabular-nums">{formatPaise(row.total_cost, currency)}</span>,
    },
    { key: 'is_active', header: 'Status',
      render: (_v: unknown, row: RecipeSummary) => (
        <Badge variant={row.is_active ? 'active' : 'cancelled'} size="sm">{row.is_active ? 'Active' : 'Inactive'}</Badge>
      ),
    },
  ], [navigate, currency]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <PageContainer
      title="Recipes"
      subtitle={`${total} recipe${total !== 1 ? 's' : ''}`}
      breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Recipes' }]}
      actions={
        <Tooltip content="Press N to add (keyboard shortcut)">
          <Button variant="primary" size="sm" onClick={() => navigate('/recipes/new')}>
            <PlusIcon /><span className="ml-1.5">New Recipe</span>
          </Button>
        </Tooltip>
      }
    >
      <div className="animate-fade-in">
        {/* KPI Row */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <KpiMini label="Total Recipes" value={totalRecipes} tone="violet" />
          <KpiMini label="Avg Food Cost" value={formatPaise(avgFoodCost, currency)} tone="brand" />
          <KpiMini label="Linked Items" value={linkedItems} tone="teal" />
        </div>

        {/* Search + Category Filter — relative z-20 lifts this card's stacking
            context above the table card below it, so the category dropdown is
            not painted under the later sibling (backdrop-blur cards each form
            their own stacking context and paint in DOM order). */}
        <div className={cn(GLASS, 'relative z-20 p-4 mb-6')}>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 max-w-md">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none text-neutral-400 dark:text-neutral-500">
                <SearchIcon />
              </div>
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                placeholder="Search recipes... (press / to focus)"
                className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-white/60 dark:bg-neutral-800/60 text-sm text-neutral-900 dark:text-neutral-100 placeholder-neutral-400 dark:placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-violet-500 transition-shadow"
                aria-label="Search recipes"
              />
            </div>
            <div className="w-full sm:w-48">
              <Select
                options={CATEGORY_OPTIONS}
                value={category}
                onChange={(val) => { setCategory(typeof val === 'string' ? val : val[0] ?? ''); setPage(1); }}
                aria-label="Filter by category"
              />
            </div>
          </div>
        </div>

        {/* Table */}
        {isLoading ? (
          <div className={cn(GLASS, 'p-6 space-y-4')}>
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="flex gap-4">
                <Skeleton variant="text" width="25%" height={18} />
                <Skeleton variant="text" width="20%" height={18} />
                <Skeleton variant="text" width="10%" height={18} />
                <Skeleton variant="text" width="10%" height={18} />
                <Skeleton variant="text" width="15%" height={18} />
                <Skeleton variant="rect" width={60} height={24} />
              </div>
            ))}
          </div>
        ) : recipes.length === 0 ? (
          <EmptyState
            variant="page"
            icon={<BookIcon />}
            title="No recipes yet"
            description="Create your first recipe to track ingredient costs"
            action={{ label: 'New Recipe', onClick: () => navigate('/recipes/new') }}
          />
        ) : (
          <>
            <div className={cn(GLASS, 'overflow-hidden')}>
              <DataTable<RecipeSummary> columns={columns} data={recipes} />
            </div>
            {totalPages > 1 && (
              <div className="mt-4">
                <Pagination currentPage={page} totalPages={totalPages} totalItems={total} pageSize={PAGE_SIZE} onPageChange={setPage} />
              </div>
            )}
          </>
        )}
      </div>
    </PageContainer>
  );
}

export function RecipeListPage() {
  return <ErrorBoundary><RecipeListContent /></ErrorBoundary>;
}
